import { toTextContent } from '@principle-theorem/editor';
import {
  ContactNumberLabel,
  IAppointmentRequest,
  IBrand,
  IPatient,
  IPatientData,
  IPatientRelationship,
  IsPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  DocumentReference,
  Firestore,
  WithRef,
  toNamedDocument,
} from '@principle-theorem/shared';
import { omit } from 'lodash';
import { Brand } from '../brand';
import { CalendarEvent } from '../event/calendar-event';
import { Patient } from '../patient/patient';
import { PatientRelationship } from '../patient/patient-relationship';
import { Recalls } from '../patient/recalls';

export interface IAppointmentRequestPatientOptions {
  selectedPatient?: WithRef<IPatient>;
  selectedPrimaryPatient?: WithRef<IPatient>;
}

export class AppointmentRequest {
  static init(
    overrides: AtLeast<
      IAppointmentRequest,
      'practitioner' | 'practice' | 'patient' | 'template' | 'event'
    >
  ): IAppointmentRequest {
    const base = CalendarEvent.init(overrides);
    return {
      ...base,
      title: [toTextContent('Appointment Request')],
      ...overrides,
    };
  }

  static async getPatientForAppointmentRequest(
    brand: WithRef<IBrand>,
    patientData: IPatientData,
    selectedPatient?: WithRef<IPatient>,
    selectedPrimaryPatient?: WithRef<IPatient>
  ): Promise<WithRef<IPatient>> {
    return AppointmentRequest._getPatientForAppointmentRequest(
      brand,
      patientData,
      {
        selectedPatient,
        selectedPrimaryPatient,
      }
    );
  }

  private static async _getPatientForAppointmentRequest(
    brand: WithRef<IBrand>,
    patientData: IPatientData,
    options: IAppointmentRequestPatientOptions
  ): Promise<WithRef<IPatient>> {
    const primaryPatient =
      options.selectedPrimaryPatient ??
      (await this._createPrimaryPatient(patientData, brand));

    if (!patientData.patient || !patientData.patient.relationshipType) {
      return primaryPatient;
    }

    const primaryContact = {
      type: patientData.patient.relationshipType,
      patient: toNamedDocument(
        primaryPatient as WithRef<IPatient & IsPrimaryContact>
      ),
    };

    const patient = options.selectedPatient
      ? await this._addPrimaryContactToPatient(
          options.selectedPatient,
          primaryContact
        )
      : await this._createPatient(patientData, brand, primaryContact);

    await PatientRelationship.addPrimaryRelationship(patient);
    return patient;
  }

  private static async _createPrimaryPatient(
    patientData: IPatientData,
    brand: WithRef<IBrand>
  ): Promise<WithRef<IPatient>> {
    const addressMetadata = patientData.primaryContact.address;
    const metadata = { address: addressMetadata };
    const address = metadata.address?.address;

    const patientInfo = Patient.init({
      ...omit(patientData.primaryContact, 'mobileNumber'),
      address,
      metadata,
      contactNumbers: [
        {
          label: ContactNumberLabel.Mobile,
          number: patientData.primaryContact.mobileNumber,
        },
      ],
      recall: Recalls.initPatientRecall(brand.settings.recalls),
      secondaryRecall: brand.settings.secondaryRecalls
        ? Recalls.initPatientRecall(brand.settings.secondaryRecalls)
        : undefined,
    });
    const patientDocRef = await AppointmentRequest._savePatient(
      brand,
      patientInfo
    );
    return Firestore.getDoc(patientDocRef);
  }

  private static async _createPatient(
    patientData: IPatientData,
    brand: WithRef<IBrand>,
    primaryContact: IPatientRelationship<IPatient & IsPrimaryContact>
  ): Promise<WithRef<IPatient>> {
    const patient = Patient.init({
      ...omit(patientData.patient, 'mobileNumber'),
      primaryContact,
      recall: Recalls.initPatientRecall(brand.settings.recalls),
      secondaryRecall: brand.settings.secondaryRecalls
        ? Recalls.initPatientRecall(brand.settings.secondaryRecalls)
        : undefined,
    });
    const patientDoc = await AppointmentRequest._savePatient(brand, patient);
    return Firestore.getDoc(patientDoc);
  }

  private static async _savePatient(
    brand: WithRef<IBrand>,
    patient: IPatient
  ): Promise<DocumentReference<IPatient>> {
    return Firestore.addDoc(Brand.patientCol(brand), patient);
  }

  private static async _addPrimaryContactToPatient(
    selectedPatient: WithRef<IPatient>,
    primaryContact: IPatientRelationship<IPatient & IsPrimaryContact>
  ): Promise<WithRef<IPatient>> {
    await Firestore.patchDoc(selectedPatient.ref, {
      primaryContact,
    });
    return { ...selectedPatient, primaryContact };
  }
}
